import { useEffect, useMemo, useState } from "react";
import { X, PaperPlaneTilt, EnvelopeSimple, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import ModalPortal from "@/components/ModalPortal";

/**
 * Modal för att skicka en momentlogg-post som mail till valda org-medlemmar.
 *
 * Props:
 *   entry — sökresultatobjektet ({entry_id, task_name, object_name, author_name})
 *   onClose — stäng-callback
 */
export default function SendLogEntryMailModal({ entry, onClose }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/task-logs/entry/${entry.entry_id}/mail-recipients`);
        if (!cancelled) setMembers(r.data?.members || []);
      } catch {
        toast.error("Kunde inte hämta mottagarlistan");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entry.entry_id]);

  const subject = useMemo(
    () => `${entry.task_name} — ${entry.object_name || "Okänt objekt"} — ${entry.author_name}`,
    [entry],
  );

  const toggle = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === members.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(members.map((m) => m.email)));
    }
  };

  const send = async () => {
    if (selected.size === 0) {
      toast.error("Välj minst en mottagare");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/task-logs/entry/${entry.entry_id}/send-mail`, {
        recipient_emails: Array.from(selected),
      });
      const { sent, failed, skipped_attachments } = r.data || {};
      if (sent > 0 && failed === 0) {
        toast.success(`Mail skickat till ${sent} mottagare`);
      } else if (sent > 0) {
        toast.warning(`${sent} skickade, ${failed} misslyckade`);
      } else {
        toast.error("Inget mail kunde skickas");
      }
      if (skipped_attachments && skipped_attachments.length > 0) {
        toast.info(`${skipped_attachments.length} bilaga(or) för stor(a) — hoppade över`);
      }
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Kunde inte skicka mail";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
        data-testid="send-log-mail-modal"
      >
        <div
          className="tk-card p-0 w-full max-w-lg max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-app flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <EnvelopeSimple size={20} className="text-brand" />
                <h2 className="font-heading text-xl leading-tight">Skicka som mail</h2>
              </div>
              <p className="text-xs text-text-muted">
                Ämnesrad genereras automatiskt. Text + bilagor från loggposten skickas.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary p-1"
              aria-label="Stäng"
              data-testid="send-log-mail-close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-5 space-y-3 overflow-y-auto flex-1">
            <div>
              <label className="text-xs uppercase tracking-wider text-text-muted">Ämnesrad</label>
              <div
                className="tk-input !py-2 !bg-surface-alt !cursor-not-allowed text-sm"
                data-testid="send-log-mail-subject"
              >
                {subject}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wider text-text-muted">
                  Mottagare i organisationen ({members.length})
                </label>
                {members.length > 0 && (
                  <button
                    onClick={toggleAll}
                    className="text-xs text-brand hover:underline"
                    data-testid="send-log-mail-toggle-all"
                  >
                    {selected.size === members.length ? "Avmarkera alla" : "Markera alla"}
                  </button>
                )}
              </div>

              {loading ? (
                <p className="text-sm text-text-muted">Laddar…</p>
              ) : members.length === 0 ? (
                <p className="text-sm text-text-muted italic">Inga mottagare i organisationen</p>
              ) : (
                <div className="border border-app rounded-lg divide-y divide-app max-h-72 overflow-y-auto">
                  {members.map((m) => {
                    const isChecked = selected.has(m.email);
                    return (
                      <label
                        key={m.email}
                        className="flex items-center gap-3 p-3 hover:bg-surface-hover cursor-pointer"
                        data-testid={`send-log-mail-recipient-${m.user_id}`}
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
                            isChecked
                              ? "bg-brand border-brand"
                              : "border-app bg-surface"
                          }`}
                        >
                          {isChecked && <Check size={12} weight="bold" className="text-white" />}
                        </div>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isChecked}
                          onChange={() => toggle(m.email)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                          <div className="text-xs text-text-muted truncate">
                            {m.email} · {m.role === "admin" ? "Admin" : "Anställd"}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="p-5 border-t border-app flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="tk-btn-secondary"
              disabled={sending}
              data-testid="send-log-mail-cancel"
            >
              Avbryt
            </button>
            <button
              onClick={send}
              disabled={selected.size === 0 || sending}
              className="tk-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="send-log-mail-send"
            >
              <PaperPlaneTilt size={16} weight="bold" />
              {sending ? "Skickar…" : `Skicka till ${selected.size || 0}`}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
